/**
 * Google Analytics 4 Event Tracking
 * Provides type-safe event tracking with support for custom events
 *
 * Works with Cloudflare Zaraz for consent management.
 * All functions check for window.gtag availability before tracking,
 * so they work automatically once Zaraz loads Google Analytics.
 */

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set',
      targetId: string | object,
      config?: object
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Signup tier types
 */
export type SignupTier = 'free' | 'pro' | 'team';

/**
 * Event categories for better organization in GA4
 */
export type EventCategory =
  | 'engagement'
  | 'conversion'
  | 'user_interaction'
  | 'navigation';

/**
 * Initialize Google Analytics
 * Call this in your app's root component or _app.tsx
 */
export function initGA(measurementId: string) {
  if (typeof window === 'undefined') return;

  // Create gtag function if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag as any;

  // Initialize GA
  gtag('js', new Date());
  gtag('config', measurementId, {
    page_path: window.location.pathname,
  });
}

/**
 * Track page views
 * @param url - Page URL
 * @param title - Page title
 */
export function trackPageView(url: string, title?: string) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.href,
  });

  // Track Google Ads page view conversion
  trackGoogleAdsPageView();
}

/**
 * Track Google Ads page view conversion
 * This fires on every page view for conversion tracking
 */
export function trackGoogleAdsPageView() {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'conversion', {
    send_to: 'AW-17730647179/snexCPuaksAbEIvp0IZC',
  });
}

/**
 * Track signup event
 * @param tier - Signup tier (free, pro, team)
 * @param metadata - Additional metadata about the signup
 */
export function trackSignup(
  tier: SignupTier,
  metadata?: {
    method?: string; // e.g., 'email', 'google', 'github'
    source?: string; // e.g., 'homepage', 'pricing', 'blog'
    campaign?: string; // UTM campaign
  }
) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const eventName = tier === 'free' ? 'sign_up_free' : tier === 'pro' ? 'sign_up_pro' : 'sign_up_team';

  // Track the generic signup event
  window.gtag('event', 'sign_up', {
    event_category: 'conversion',
    event_label: tier,
    tier,
    method: metadata?.method || 'unknown',
    source: metadata?.source || 'unknown',
    campaign: metadata?.campaign,
    value: tier === 'free' ? 0 : tier === 'pro' ? 1 : 2, // Relative value
  });

  // Track the tier-specific event
  window.gtag('event', eventName, {
    event_category: 'conversion',
    event_label: tier,
    tier,
    method: metadata?.method || 'unknown',
    source: metadata?.source || 'unknown',
    campaign: metadata?.campaign,
    value: tier === 'free' ? 0 : tier === 'pro' ? 1 : 2,
  });

  console.log(`[Analytics] Tracked signup: ${eventName}`, { tier, metadata });
}

/**
 * Track custom event
 * @param eventName - Name of the event
 * @param params - Event parameters
 */
export function trackEvent(
  eventName: string,
  params?: {
    event_category?: EventCategory | string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  }
) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, params);

  console.log(`[Analytics] Tracked event: ${eventName}`, params);
}

/**
 * Track newsletter signup
 * @param source - Where the signup came from
 */
export function trackNewsletterSignup(source?: string) {
  trackEvent('newsletter_signup', {
    event_category: 'conversion',
    event_label: source || 'unknown',
    source,
  });
}

/**
 * Track content download
 * @param contentType - Type of content (pdf, template, code, etc.)
 * @param contentName - Name of the content
 */
export function trackDownload(contentType: string, contentName: string) {
  trackEvent('download', {
    event_category: 'engagement',
    event_label: contentType,
    content_type: contentType,
    content_name: contentName,
  });
}

/**
 * Track search
 * @param searchTerm - Search query
 * @param results - Number of results
 */
export function trackSearch(searchTerm: string, results?: number) {
  trackEvent('search', {
    event_category: 'engagement',
    event_label: searchTerm,
    search_term: searchTerm,
    results: results !== undefined ? results : null,
  });
}

/**
 * Track link click (external or internal)
 * @param url - Link URL
 * @param text - Link text
 * @param type - Link type ('internal' | 'external')
 */
export function trackLinkClick(url: string, text?: string, type: 'internal' | 'external' = 'external') {
  trackEvent('click', {
    event_category: 'engagement',
    event_label: type,
    link_url: url,
    link_text: text,
    link_type: type,
  });
}

/**
 * Track video play
 * @param videoTitle - Video title
 * @param videoUrl - Video URL
 */
export function trackVideoPlay(videoTitle: string, videoUrl?: string) {
  trackEvent('video_play', {
    event_category: 'engagement',
    event_label: videoTitle,
    video_title: videoTitle,
    video_url: videoUrl,
  });
}

/**
 * Track form submission
 * @param formName - Name of the form
 * @param formType - Type of form (contact, feedback, etc.)
 */
export function trackFormSubmit(formName: string, formType?: string) {
  trackEvent('form_submit', {
    event_category: 'engagement',
    event_label: formName,
    form_name: formName,
    form_type: formType,
  });
}

/**
 * Track error
 * @param errorMessage - Error message
 * @param errorType - Type of error
 * @param fatal - Whether the error is fatal
 */
export function trackError(errorMessage: string, errorType?: string, fatal: boolean = false) {
  trackEvent('error', {
    event_category: 'engagement',
    event_label: errorType || 'unknown',
    error_message: errorMessage,
    error_type: errorType,
    fatal,
  });
}

/**
 * Track conversion
 * @param conversionId - Conversion identifier
 * @param value - Monetary value of the conversion
 * @param currency - Currency code (default: USD)
 */
export function trackConversion(conversionId: string, value?: number, currency: string = 'USD') {
  trackEvent('conversion', {
    event_category: 'conversion',
    event_label: conversionId,
    conversion_id: conversionId,
    value,
    currency,
  });
}

/**
 * Set user properties
 * @param properties - User properties to set
 */
export function setUserProperties(properties: {
  user_id?: string;
  tier?: SignupTier;
  is_premium?: boolean;
  [key: string]: any;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('set', 'user_properties', properties);

  console.log('[Analytics] Set user properties:', properties);
}

/**
 * Track timing
 * @param category - Timing category
 * @param variable - Timing variable
 * @param value - Time value in milliseconds
 * @param label - Optional label
 */
export function trackTiming(
  category: string,
  variable: string,
  value: number,
  label?: string
) {
  trackEvent('timing_complete', {
    event_category: category,
    event_label: label,
    name: variable,
    value,
  });
}

/**
 * Helper: Get UTM parameters from URL
 */
export function getUTMParams(): {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
} {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);

  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    term: params.get('utm_term') || undefined,
    content: params.get('utm_content') || undefined,
  };
}

/**
 * Helper: Track outbound link click
 * @param url - Outbound URL
 * @param text - Link text
 */
export function trackOutboundLink(url: string, text?: string) {
  trackEvent('click', {
    event_category: 'engagement',
    event_label: 'outbound',
    link_url: url,
    link_text: text,
    outbound: true,
  });
}

/**
 * Track purchase/revenue
 * This is the main ecommerce event for GA4
 * @param transaction - Transaction details
 */
export function trackPurchase(transaction: {
  transaction_id: string;
  value: number;
  currency?: string;
  tax?: number;
  shipping?: number;
  items?: Array<{
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
    quantity?: number;
  }>;
  coupon?: string;
  affiliation?: string;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const {
    transaction_id,
    value,
    currency = 'USD',
    tax,
    shipping,
    items = [],
    coupon,
    affiliation,
  } = transaction;

  window.gtag('event', 'purchase', {
    transaction_id,
    value,
    currency,
    tax,
    shipping,
    items,
    coupon,
    affiliation,
  });

  console.log(`[Analytics] Tracked purchase: ${transaction_id}`, { value, currency });
}

/**
 * Track subscription start (for recurring revenue)
 * @param subscription - Subscription details
 */
export function trackSubscription(subscription: {
  subscription_id: string;
  tier: SignupTier;
  value: number; // Monthly recurring revenue
  currency?: string;
  billing_period?: 'monthly' | 'yearly';
  trial_period_days?: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const {
    subscription_id,
    tier,
    value,
    currency = 'USD',
    billing_period = 'monthly',
    trial_period_days,
  } = subscription;

  // Track as a purchase event for revenue
  window.gtag('event', 'purchase', {
    transaction_id: subscription_id,
    value,
    currency,
    items: [
      {
        item_id: `subscription_${tier}`,
        item_name: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Subscription`,
        item_category: 'subscription',
        price: value,
        quantity: 1,
      },
    ],
    affiliation: 'subscription',
  });

  // Also track as a subscription event for clarity
  window.gtag('event', 'start_subscription', {
    event_category: 'conversion',
    event_label: tier,
    subscription_id,
    tier,
    value,
    currency,
    billing_period,
    trial_period_days,
  });

  console.log(`[Analytics] Tracked subscription: ${tier}`, { value, currency, billing_period });
}

/**
 * Track subscription cancellation
 * @param subscription - Subscription details
 */
export function trackSubscriptionCancel(subscription: {
  subscription_id: string;
  tier: SignupTier;
  cancel_reason?: string;
  had_value?: number; // Lifetime value before cancellation
}) {
  trackEvent('cancel_subscription', {
    event_category: 'conversion',
    event_label: subscription.tier,
    subscription_id: subscription.subscription_id,
    tier: subscription.tier,
    cancel_reason: subscription.cancel_reason,
    had_value: subscription.had_value,
  });
}

/**
 * Track refund
 * @param refund - Refund details
 */
export function trackRefund(refund: {
  transaction_id: string;
  value: number;
  currency?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity?: number;
  }>;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const { transaction_id, value, currency = 'USD', items = [] } = refund;

  window.gtag('event', 'refund', {
    transaction_id,
    value,
    currency,
    items,
  });

  console.log(`[Analytics] Tracked refund: ${transaction_id}`, { value, currency });
}

/**
 * Track upgrade (from one tier to another)
 * @param upgrade - Upgrade details
 */
export function trackUpgrade(upgrade: {
  from_tier: SignupTier;
  to_tier: SignupTier;
  value_change: number; // Difference in price (monthly)
  currency?: string;
}) {
  const { from_tier, to_tier, value_change, currency = 'USD' } = upgrade;

  trackEvent('upgrade', {
    event_category: 'conversion',
    event_label: `${from_tier}_to_${to_tier}`,
    from_tier,
    to_tier,
    value: value_change,
    currency,
  });

  console.log(`[Analytics] Tracked upgrade: ${from_tier} → ${to_tier}`, { value_change });
}

/**
 * Track downgrade (from one tier to another)
 * @param downgrade - Downgrade details
 */
export function trackDowngrade(downgrade: {
  from_tier: SignupTier;
  to_tier: SignupTier;
  value_change: number; // Difference in price (monthly, will be negative)
  currency?: string;
  downgrade_reason?: string;
}) {
  const { from_tier, to_tier, value_change, currency = 'USD', downgrade_reason } = downgrade;

  trackEvent('downgrade', {
    event_category: 'conversion',
    event_label: `${from_tier}_to_${to_tier}`,
    from_tier,
    to_tier,
    value: Math.abs(value_change),
    currency,
    downgrade_reason,
  });

  console.log(`[Analytics] Tracked downgrade: ${from_tier} → ${to_tier}`, { value_change });
}

/**
 * Begin checkout event
 * Tracks when user starts the checkout process
 */
export function trackBeginCheckout(checkout: {
  tier: SignupTier;
  value: number;
  currency?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
  }>;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const { tier, value, currency = 'USD', items = [] } = checkout;

  window.gtag('event', 'begin_checkout', {
    event_category: 'conversion',
    event_label: tier,
    tier,
    value,
    currency,
    items,
  });

  console.log(`[Analytics] Begin checkout: ${tier}`, { value, currency });
}

/**
 * Track trial start
 * @param trial - Trial details
 */
export function trackTrialStart(trial: {
  tier: SignupTier;
  trial_period_days: number;
  expected_value: number; // Value after trial converts
  currency?: string;
}) {
  trackEvent('start_trial', {
    event_category: 'conversion',
    event_label: trial.tier,
    tier: trial.tier,
    trial_period_days: trial.trial_period_days,
    value: trial.expected_value,
    currency: trial.currency || 'USD',
  });

  console.log(`[Analytics] Trial started: ${trial.tier}`, {
    days: trial.trial_period_days,
    expected_value: trial.expected_value,
  });
}

/**
 * Track trial conversion (trial → paid)
 * @param conversion - Trial conversion details
 */
export function trackTrialConversion(conversion: {
  tier: SignupTier;
  value: number;
  currency?: string;
  trial_duration_days: number;
}) {
  trackEvent('trial_conversion', {
    event_category: 'conversion',
    event_label: conversion.tier,
    tier: conversion.tier,
    value: conversion.value,
    currency: conversion.currency || 'USD',
    trial_duration_days: conversion.trial_duration_days,
  });

  console.log(`[Analytics] Trial converted: ${conversion.tier}`, {
    value: conversion.value,
  });
}
